import { useEffect } from 'react';
import { isNotNullish, isNullish } from '@chainflip/utils/guard';
import { useMutation } from '@tanstack/react-query';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useEvmScAccount from './useEvmScAccount';
import { getEvmStateChainCall } from '../graphql/explorer/scCall';

export default function useEvmScCall<T>({
  onSuccess,
  onSubmitted,
  onEvmError,
  onScError,
  fn,
}: {
  onSuccess: VoidFunction;
  onEvmError: (error: Error) => void;
  onSubmitted: (result: { txHash: `0x${string}`; args: T }) => void;
  onScError: (error: { name: string; docs: string }) => void;
  fn: (args: T) => Promise<`0x${string}`>;
}) {
  const { refresh } = useEvmScAccount();

  const {
    data: evmData,
    mutate,
    isPending,
    reset,
  } = useMutation({
    mutationFn: async (args: T) => {
      const txHash = await fn(args);

      return { txHash, args };
    },
    onSuccess: onSubmitted,
    onError: onEvmError,
  });

  const { data } = useGqlQuery(getEvmStateChainCall, {
    variables: { txHash: evmData?.txHash as string },
    enabled: evmData?.txHash !== undefined,
    context: { clientName: 'processor' },
    refetchInterval: 5_000,
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    if (!data?.call) return;

    reset();
    refresh?.();

    if (data.call.success) {
      onSuccess();
    } else {
      onScError(
        data.call.error ?? { name: 'IncorrectPayload', docs: 'An error occurred on state chain' },
      );
    }
  }, [data?.call, evmData?.txHash]);

  return {
    mutate,
    isPending: isPending || (isNotNullish(evmData?.txHash) && isNullish(data?.call)),
  };
}
